// src/components/business/BusinessEmailSettings.js
import React, { useState, useEffect } from 'react';
import { 
  Save, AlertCircle, Mail, CheckCircle, Loader,
  Info, Shield, RefreshCw, Settings, User
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase/config';
import { functions } from '../../../firebase/config';
import { httpsCallable } from "firebase/functions";
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const BusinessEmailSettings = ({ businessId }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [business, setBusiness] = useState(null);

  const saveGmailSettings = httpsCallable(functions, 'saveGmailSettings');
  
  const [emailSettings, setEmailSettings] = useState({
    senderName: '',
    senderEmail: '',
    replyToEmail: '',
    isVerified: false,
    verificationStatus: 'pending', // pending, verified, failed
    useGmail: false,
    useSmtp: false,
    useDefaultProvider: true,
    smtpSettings: {
      host: '',
      port: '',
      username: '',
      password: '',
      useSsl: true
    },
    defaultFooter: '',
    unsubscribeLink: true,
    maxDailyEmails: 100
  });
  
  // Load existing email settings
  useEffect(() => {
    const fetchEmailSettings = async () => {
      if (!businessId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        
        if (businessDoc.exists()) {
          const data = businessDoc.data();
          setBusiness(data);
          
          if (data.emailSettings) {
            setEmailSettings({
              ...data.emailSettings,
              useGmail: !!data.emailSettings.useGmail,
              useSmtp: !!data.emailSettings.useSmtp,
              useDefaultProvider: !data.emailSettings.useGmail && !data.emailSettings.useSmtp,
            });
          }
        }
      } catch (err) {
        console.error("Error loading email settings:", err);
        setError("Failed to load email settings. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmailSettings();
  }, [businessId]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('smtp.')) {
      const smtpField = name.split('.')[1];
      setEmailSettings(prev => ({
        ...prev,
        smtpSettings: {
          ...prev.smtpSettings,
          [smtpField]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setEmailSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear any error messages
    setError(null);
    setSuccess(null);
  };

  // Handle email provider change
  const handleEmailProviderChange = (provider) => {
    // Update the email settings with the new provider
    setEmailSettings(prev => ({
      ...prev,
      useGmail: provider === 'gmail',
      useSmtp: provider === 'smtp',
      useDefaultProvider: provider === 'default'
    }));
    
    // Clear any previous messages
    setError(null);
    setSuccess(null);
  };
  
  // Test SMTP connection
  const testSmtpConnection = async () => {
    // Validate SMTP settings
    if (!emailSettings.smtpSettings.host) {
      setError("SMTP Host is required");
      return;
    }
    
    if (!emailSettings.smtpSettings.port) {
      setError("SMTP Port is required");
      return;
    }
    
    if (!emailSettings.smtpSettings.username) {
      setError("SMTP Username is required");
      return;
    }
    
    if (!emailSettings.smtpSettings.password) {
      setError("SMTP Password is required");
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // Make a request to your backend to test the SMTP connection
      const response = await fetch('/api/email/test-smtp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          host: emailSettings.smtpSettings.host,
          port: emailSettings.smtpSettings.port,
          username: emailSettings.smtpSettings.username,
          password: emailSettings.smtpSettings.password,
          useSsl: emailSettings.smtpSettings.useSsl
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSuccess("SMTP connection successful! Your email settings are correctly configured.");
      } else {
        setError(`SMTP connection failed: ${result.message}`);
      }
    } catch (err) {
      console.error("Error testing SMTP connection:", err);
      setError("Failed to test SMTP connection. Please check your settings and try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Test Gmail connection
  const testGmailConnection = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // In a real app, this would call an API endpoint to test the Gmail connection
      const response = await fetch(`/api/email/test-gmail/${businessId}`, {
        method: 'POST',
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSuccess("Gmail connection successful! Your email settings are correctly configured.");
      } else {
        setError(`Gmail connection failed: ${result.message}`);
      }
    } catch (err) {
      console.error("Error testing Gmail connection:", err);
      setError("Failed to test Gmail connection. Please check your settings and try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const [gmailSettings, setGmailSettings] = useState({
    email: '',
    appPassword: ''
  });
  
  // Add a function to save Gmail settings
  const handleSaveGmailSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!gmailSettings.email) {
        setError("Gmail email is required");
        setLoading(false);
        return;
      }
      
      if (!gmailSettings.appPassword) {
        setError("Gmail app password is required");
        setLoading(false);
        return;
      }

    const saveGmailSettingsResult = await saveGmailSettings({
      businessId: businessId,
      email: gmailSettings.email,
      appPassword: gmailSettings.appPassword
    });
      
      setSuccess("Gmail settings saved and connected successfully");
    } catch (error) {
      console.error("Error saving Gmail settings:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Verify sender email
  const handleVerifySender = async () => {
    try {
      setVerifying(true);
      setError(null);
      setSuccess(null);
      
      if (!emailSettings.senderEmail) {
        setError("Sender email is required");
        setVerifying(false);
        return;
      }
      
      // In a real app, this would call your backend API to trigger
      // a verification process with SendGrid or your email provider
      // For now, we'll simulate it
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update verification status
      setEmailSettings(prev => ({
        ...prev,
        verificationStatus: 'verifying',
        verificationMessage: 'A verification email has been sent. Please check your inbox.'
      }));
      
      setSuccess("Verification email sent. Please check your inbox.");
      
      // In a real app, you would update this status when the email is actually verified
    } catch (err) {
      console.error("Error verifying sender:", err);
      setError("Failed to start verification: " + err.message);
    } finally {
      setVerifying(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="animate-spin h-8 w-8 text-[#190192]" />
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-xl font-medium text-gray-900 mb-6">Email Sending Settings</h2>
      
      {/* Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 text-red-700 flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}
      
      {success && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 text-green-700 flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}
    
      <div className="space-y-6">
        {/* Sender Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sender Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="senderName" className="block text-sm font-medium text-gray-700 mb-1">
                Sender Name <span className="text-red-500">*</span>
              </label>
              <input
                id="senderName"
                name="senderName"
                type="text"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                value={emailSettings.senderName}
                onChange={handleInputChange}
                placeholder="Your Business Name"
              />
              <p className="mt-1 text-xs text-gray-500">
                This name will appear as the sender of your emails
              </p>
            </div>
            
            <div>
              <label htmlFor="senderEmail" className="block text-sm font-medium text-gray-700 mb-1">
                Sender Email <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <input
                  id="senderEmail"
                  name="senderEmail"
                  type="email"
                  className="block w-full rounded-l-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                  value={emailSettings.senderEmail}
                  onChange={handleInputChange}
                  placeholder="email@yourdomain.com"
                />
                <button
                  type="button"
                  onClick={handleVerifySender}
                  disabled={verifying || !emailSettings.senderEmail}
                  className={`px-4 py-2 rounded-r-lg text-white ${
                    verifying || !emailSettings.senderEmail ? 'bg-gray-400' : 'bg-[#190192] hover:bg-[#2C0DB5]'
                  } flex items-center`}
                >
                  {verifying ? (
                    <Loader className="animate-spin h-4 w-4 mr-1" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  Verify
                </button>
              </div>
              
              {/* Verification status */}
              {emailSettings.verificationStatus === 'verified' && (
                <p className="mt-1 text-xs text-green-600 flex items-center">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Verified
                </p>
              )}
              
              {emailSettings.verificationStatus === 'verifying' && (
                <p className="mt-1 text-xs text-blue-600 flex items-center">
                  <Info className="h-3 w-3 mr-1" />
                  {emailSettings.verificationMessage}
                </p>
              )}
              
              {emailSettings.verificationStatus === 'failed' && (
                <p className="mt-1 text-xs text-red-600 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Verification failed. Please try again.
                </p>
              )}
              
              <p className="mt-1 text-xs text-gray-500">
                Must be verified before sending emails
              </p>
            </div>
          </div>
          
          <div className="mt-4">
            <label htmlFor="replyToEmail" className="block text-sm font-medium text-gray-700 mb-1">
              Reply-To Email
            </label>
            <input
              id="replyToEmail"
              name="replyToEmail"
              type="email"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
              value={emailSettings.replyToEmail}
              onChange={handleInputChange}
              placeholder="replies@yourdomain.com"
            />
            <p className="mt-1 text-xs text-gray-500">
              If left empty, replies will go to the sender email
            </p>
          </div>
        </div>
        
        {/* Email Provider Section */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Email Provider</h3>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                id="defaultProvider"
                name="emailProvider"
                type="radio"
                value="default"
                className="h-4 w-4 text-[#190192] focus:ring-[#190192] border-gray-300"
                checked={!emailSettings.useGmail && !emailSettings.useSmtp}
                onChange={() => handleEmailProviderChange('default')}
              />
              <label htmlFor="defaultProvider" className="ml-2 block text-sm text-gray-700">
                Use default email service
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="gmailProvider"
                name="emailProvider"
                type="radio"
                value="gmail"
                className="h-4 w-4 text-[#190192] focus:ring-[#190192] border-gray-300"
                checked={emailSettings.useGmail}
                onChange={() => handleEmailProviderChange('gmail')}
              />
              <label htmlFor="gmailProvider" className="ml-2 block text-sm text-gray-700">
                Use Gmail / Google Workspace
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="smtpProvider"
                name="emailProvider"
                type="radio"
                value="smtp"
                className="h-4 w-4 text-[#190192] focus:ring-[#190192] border-gray-300"
                checked={emailSettings.useSmtp}
                onChange={() => handleEmailProviderChange('smtp')}
              />
              <label htmlFor="smtpProvider" className="ml-2 block text-sm text-gray-700">
                Use custom SMTP server
              </label>
            </div>
          </div>
          
          {/* Gmail Integration Section */}
          {emailSettings.useGmail && (
            <>
              {business?.gmailCredentials ? (
                <div className="mt-4 bg-green-50 p-4 rounded-lg flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Gmail Connected</p>
                    <p className="text-sm text-green-700 mt-1">
                      Emails will be sent via {business.gmailCredentials.email}
                    </p>
                    <div className="mt-2 flex space-x-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/communication/email/gmail/${businessId}`)}
                        className="text-sm text-[#190192] hover:text-[#2C0DB5]"
                      >
                        Edit Configuration
                      </button>
                      <button
                        type="button"
                        onClick={testGmailConnection}
                        className="text-sm text-[#190192] hover:text-[#2C0DB5]"
                      >
                        Test Connection
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-4 bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-700">
                    Connect your Gmail or Google Workspace account to send emails through your own email address.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(`/communication/email/gmail/${businessId}`)}
                    className="mt-2 px-4 py-2 bg-[#190192] text-white rounded-lg text-sm"
                  >
                    Connect Gmail Account
                  </button>
                </div>
              )}
            </>
          )}

          <div className="mt-6 p-4 border rounded-lg">
            <h3 className="text-lg font-medium mb-4">Gmail Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Gmail Address</label>
                <input
                  type="email"
                  value={gmailSettings.email}
                  onChange={(e) => setGmailSettings({...gmailSettings, email: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">App Password</label>
                <input
                  type="password"
                  value={gmailSettings.appPassword}
                  onChange={(e) => setGmailSettings({...gmailSettings, appPassword: e.target.value})}
                  className="w-full p-2 border rounded"
                />
                <p className="text-xs text-gray-500 mt-1">
                  <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">
                    How to generate an app password
                  </a>
                </p>
              </div>
            </div>
            <div className="mt-4">
              <button
                onClick={handleSaveGmailSettings}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              >
                {loading ? 'Saving...' : 'Save Gmail Settings'}
              </button>
            </div>
          </div>
          
          {/* SMTP Settings Section */}
          {emailSettings.useSmtp && (
            <div className="mt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Advanced SMTP Settings</h3>
              <p className="text-sm text-gray-600 mb-4">
                <Info className="h-4 w-4 inline mr-1 text-blue-500" />
                Configure these settings to connect to your custom SMTP server.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="smtp.host" className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Host <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="smtp.host"
                    name="smtp.host"
                    type="text"
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                    value={emailSettings.smtpSettings.host}
                    onChange={handleInputChange}
                    placeholder="smtp.yourdomain.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="smtp.port" className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Port <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="smtp.port"
                    name="smtp.port"
                    type="text"
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                    value={emailSettings.smtpSettings.port}
                    onChange={handleInputChange}
                    placeholder="587 or 465"
                  />
                </div>
                
                <div>
                  <label htmlFor="smtp.username" className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="smtp.username"
                    name="smtp.username"
                    type="text"
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                    value={emailSettings.smtpSettings.username}
                    onChange={handleInputChange}
                    placeholder="username@yourdomain.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="smtp.password" className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="smtp.password"
                    name="smtp.password"
                    type="password"
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                    value={emailSettings.smtpSettings.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                  />
                </div>
              </div>
              
              <div className="mt-4 flex items-center">
                <input
                  id="smtp.useSsl"
                  name="smtp.useSsl"
                  type="checkbox"
                  className="h-4 w-4 text-[#190192] focus:ring-[#190192] border-gray-300 rounded"
                  checked={emailSettings.smtpSettings.useSsl}
                  onChange={handleInputChange}
                />
                <label htmlFor="smtp.useSsl" className="ml-2 block text-sm text-gray-700">
                  Use SSL/TLS
                </label>
              </div>
              
              <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
                <div className="flex items-start">
                  <Shield className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-700">
                    Your SMTP password is encrypted before being stored and is never visible to our team.
                  </p>
                </div>
              </div>
              
              <div className="mt-4">
                <button
                  type="button"
                  onClick={testSmtpConnection}
                  className="px-4 py-2 border border-[#190192] text-[#190192] rounded-lg hover:bg-purple-50 font-medium flex items-center"
                  disabled={loading}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Test SMTP Connection
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Email Defaults */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Email Defaults</h3>
          
          <div>
            <label htmlFor="defaultFooter" className="block text-sm font-medium text-gray-700 mb-1">
              Default Email Footer
            </label>
            <textarea
              id="defaultFooter"
              name="defaultFooter"
              rows="4"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
              value={emailSettings.defaultFooter}
              onChange={handleInputChange}
              placeholder="Footer text to be included in all emails..."
            />
            <p className="mt-1 text-xs text-gray-500">
              This will be appended to all emails (HTML supported)
            </p>
          </div>
          
          <div className="mt-4 flex items-center">
            <input
              id="unsubscribeLink"
              name="unsubscribeLink"
              type="checkbox"
              className="h-4 w-4 text-[#190192] focus:ring-[#190192] border-gray-300 rounded"
              checked={emailSettings.unsubscribeLink}
              onChange={handleInputChange}
            />
            <label htmlFor="unsubscribeLink" className="ml-2 block text-sm text-gray-700">
              Include unsubscribe link in marketing emails
            </label>
          </div>
          
          <div className="mt-4">
            <label htmlFor="maxDailyEmails" className="block text-sm font-medium text-gray-700 mb-1">
              Daily Email Limit
            </label>
            <input
              id="maxDailyEmails"
              name="maxDailyEmails"
              type="number"
              min="0"
              className="block w-48 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
              value={emailSettings.maxDailyEmails}
              onChange={handleInputChange}
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum number of emails to send per day (0 = unlimited)
            </p>
          </div>
        </div>
      </div>
      
      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={handleSaveGmailSettings}
          disabled={saving}
          className={`px-6 py-2.5 rounded-lg text-white ${
            saving ? 'bg-gray-400' : 'bg-[#190192] hover:bg-[#2C0DB5]'
          } flex items-center`}
        >
          {saving ? (
            <>
              <Loader className="animate-spin h-4 w-4 mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BusinessEmailSettings;